import { useCallback, useEffect, useMemo, useState } from "react";
import { CLIENT_TYPE_LABELS } from "@/lib/clientTypeLabels";
import {
  createChildTable,
  createPriceTable,
  deletePriceTable,
  exportPriceTablePdf,
  exportPriceTableXlsx,
  exportSegmentComparisonXlsx,
  fetchEntryPriceHistory,
  fetchPriceTable,
  fetchPriceTables,
  migrateCustomers,
  propagateToChildren,
  updatePriceTable,
} from "@/lib/adminPriceTablesApi";
import { fetchAdminProducts } from "@/lib/adminProductsApi";
import { fetchCatalogUnits, fetchCatalogMetadata } from "@/lib/catalogApi";
import type {
  AdminProductSummary,
  ClientType,
  CatalogUnit,
  CatalogCutType,
  PriceChange,
  PriceTable,
  PriceTableDetail,
  PriceTableEntry,
} from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowRight,
  ArrowRightLeft,
  Building2,
  Check,
  ChevronsUpDown,
  Download,
  FileSpreadsheet,
  FileText,
  GitBranch,
  History,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Star,
  Trash2,
  Users,
  X,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { partitionDefaultTables } from "@/lib/priceTablePartition";

type DraftMeta = {
  name: string;
  scope: ClientType;
  active: boolean;
};

// Valor sentinela do seletor de corte para "sem corte (base)" — Radix Select não aceita value "".
const BASE_CUT_VALUE = "__base__";

type DraftRow = {
  key: string;
  /** id da entrada já persistida; ausente em linhas novas (sem histórico ainda). */
  entryId?: string;
  productId: string;
  unitId: string;
  /** id do corte; "" = preço base (sem corte). */
  cutTypeId: string;
  price: string;
  isNew: boolean;
  isOverride: boolean;
};

export default function AdminPriceTables() {
  const [query, setQuery] = useState("");
  const [segmentFilter, setSegmentFilter] = useState<ClientType | "ALL">("ALL");
  const [priceTables, setPriceTables] = useState<PriceTable[]>([]);
  const [priceTablesStatus, setPriceTablesStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [priceTablesError, setPriceTablesError] = useState<string | null>(null);
  const [products, setProducts] = useState<AdminProductSummary[]>([]);
  const [catalogUnits, setCatalogUnits] = useState<CatalogUnit[]>([]);
  const [catalogCutTypes, setCatalogCutTypes] = useState<CatalogCutType[]>([]);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [listCreateOpen, setListCreateOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [editorTable, setEditorTable] = useState<PriceTableDetail | null>(null);
  const [editorBaseline, setEditorBaseline] = useState<PriceTableDetail | null>(null);
  const [draftMeta, setDraftMeta] = useState<DraftMeta | null>(null);
  const [draftRows, setDraftRows] = useState<DraftRow[]>([]);
  const [productSearch, setProductSearch] = useState("");
  const [propagateConfirmOpen, setPropagateConfirmOpen] = useState(false);
  const [childCreateOpen, setChildCreateOpen] = useState(false);
  const [migrateOpen, setMigrateOpen] = useState(false);

  const loadPriceTables = useCallback(async () => {
    setPriceTablesStatus("loading");
    setPriceTablesError(null);
    try {
      const tables = await fetchPriceTables();
      setPriceTables(tables);
      setPriceTablesStatus("ready");
    } catch (error) {
      setPriceTables([]);
      setPriceTablesStatus("error");
      setPriceTablesError(error instanceof Error ? error.message : "Nao foi possivel carregar as tabelas de preço");
    }
  }, []);

  const loadMetadata = useCallback(async () => {
    setMetadataError(null);
    try {
      const [productSummaries, units, metadata] = await Promise.all([
        fetchAdminProducts(),
        fetchCatalogUnits(),
        fetchCatalogMetadata(),
      ]);
      setProducts(productSummaries);
      setCatalogUnits(units);
      setCatalogCutTypes(metadata.cutTypes);
    } catch (error) {
      setProducts([]);
      setCatalogUnits([]);
      setCatalogCutTypes([]);
      setMetadataError(error instanceof Error ? error.message : "Nao foi possivel carregar os metadados de edição");
    }
  }, []);

  useEffect(() => {
    void loadPriceTables();
    void loadMetadata();
  }, [loadMetadata, loadPriceTables]);

  const unitById = useMemo(
    () => new Map(catalogUnits.map((unit) => [unit.id, unit] as const)),
    [catalogUnits],
  );

  const unitByName = useMemo(
    () => new Map(catalogUnits.map((unit) => [unit.name, unit] as const)),
    [catalogUnits],
  );

  const orderedProducts = useMemo(
    () => products.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [products],
  );

  // Produtos inativos não podem ser adicionados a tabelas de preço — só ofertar ativos no seletor.
  // A resolução de nome das linhas existentes continua usando `orderedProducts` (lista completa).
  const selectableProducts = useMemo(
    () => orderedProducts.filter((product) => product.active),
    [orderedProducts],
  );

  const filteredTables = useMemo(() => {
    const q = query.trim().toLowerCase();
    return priceTables.filter((table) => {
      if (segmentFilter !== "ALL" && table.scope !== segmentFilter) return false;
      if (!q) return true;
      return table.name.toLowerCase().includes(q) || CLIENT_TYPE_LABELS[table.scope].toLowerCase().includes(q);
    });
  }, [priceTables, query, segmentFilter]);

  // Tabelas Padrão (base do segmento) ficam fixas e destacadas no topo, separadas das demais.
  const { defaults: defaultTables, others: otherTables } = useMemo(
    () => partitionDefaultTables(filteredTables),
    [filteredTables],
  );

  const tableCountByScope = useMemo(() => {
    const counts = new Map<ClientType, number>();
    for (const table of priceTables) {
      counts.set(table.scope, (counts.get(table.scope) ?? 0) + 1);
    }
    return counts;
  }, [priceTables]);

  const linkedClients = editorTable?.linkedCustomers ?? [];
  const linkedDeliveryUnits = editorTable?.linkedDeliveryUnits ?? [];
  const isChildTable = !!editorTable?.parentId;
  const hasChildren = (editorTable?.children?.length ?? 0) > 0;

  const cutTypeByName = useMemo(
    () => new Map(catalogCutTypes.map((cut) => [cut.name, cut] as const)),
    [catalogCutTypes],
  );
  const cutTypeById = useMemo(
    () => new Map(catalogCutTypes.map((cut) => [cut.id, cut] as const)),
    [catalogCutTypes],
  );

  // Cortes disponíveis do produto (nome → id), para o seletor de corte de cada linha.
  const getCutOptionsForProduct = (product: AdminProductSummary): CatalogCutType[] => {
    return (product.availableCuts ?? [])
      .map((name) => cutTypeByName.get(name))
      .filter((cut): cut is CatalogCutType => !!cut);
  };

  const getUnitOptionsForProduct = (product: AdminProductSummary) => {
    const unitNames = [product.defaultUnit, ...(product.availableUnits ?? [])];
    const seen = new Set<string>();
    return unitNames
      .map((name) => unitByName.get(name))
      .filter((unit): unit is CatalogUnit => !!unit)
      .filter((unit) => {
        if (seen.has(unit.id)) return false;
        seen.add(unit.id);
        return true;
      });
  };

  const createNewDraftRow = (): DraftRow => ({
    key: crypto.randomUUID(),
    productId: "",
    unitId: "",
    cutTypeId: "",
    price: "",
    isNew: true,
    isOverride: false,
  });

  const mapDetailToRows = (detail: PriceTableDetail): DraftRow[] =>
    detail.entries.map((entry) => ({
      key: `${entry.productId}-${entry.unitId}-${entry.cutTypeId ?? "base"}-${crypto.randomUUID()}`,
      entryId: entry.id,
      productId: entry.productId,
      unitId: entry.unitId,
      cutTypeId: entry.cutTypeId ?? "",
      price: entry.price.toFixed(2),
      isNew: false,
      isOverride: entry.isOverride ?? false,
    }));

  const normalizeRows = (rows: DraftRow[]) =>
    rows
      .map((row) => ({
        productId: row.productId,
        unitId: row.unitId,
        cutTypeId: row.cutTypeId,
        price: Number.parseFloat(row.price.replace(",", ".")).toFixed(2),
        isOverride: row.isOverride,
      }))
      .sort((a, b) =>
        a.productId.localeCompare(b.productId) ||
        a.unitId.localeCompare(b.unitId) ||
        a.cutTypeId.localeCompare(b.cutTypeId) ||
        a.price.localeCompare(b.price),
      );

  const baselineRows = useMemo(
    () => (editorBaseline ? normalizeRows(mapDetailToRows(editorBaseline)) : []),
    [editorBaseline],
  );

  const currentRows = useMemo(() => normalizeRows(draftRows), [draftRows]);

  // Filtro apenas de EXIBIÇÃO por nome do produto — não muta draftRows (fonte de verdade do save).
  // Linhas novas / sem produto selecionado permanecem visíveis para poderem ser preenchidas.
  const visibleRows = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return draftRows;
    return draftRows.filter((row) => {
      if (row.isNew || !row.productId) return true;
      const product = orderedProducts.find((item) => item.id === row.productId);
      if (!product) return false;
      return (
        product.name.toLowerCase().includes(q) ||
        (product.code != null && String(product.code).includes(q))
      );
    });
  }, [draftRows, productSearch, orderedProducts]);

  const openEditor = async (id: string) => {
    setEditorOpen(true);
    setEditorLoading(true);
    setEditorError(null);
    setEditorTable(null);
    setEditorBaseline(null);
    setDraftMeta(null);
    setDraftRows([]);
    setProductSearch("");

    try {
      const detail = await fetchPriceTable(id);
      setEditorTable(detail);
      setEditorBaseline(detail);
      setDraftMeta({ name: detail.name, scope: detail.scope, active: detail.active });
      setDraftRows(mapDetailToRows(detail));
    } catch (error) {
      setEditorError(error instanceof Error ? error.message : "Nao foi possivel carregar a tabela");
    } finally {
      setEditorLoading(false);
    }
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditorLoading(false);
    setEditorSaving(false);
    setEditorError(null);
    setEditorTable(null);
    setEditorBaseline(null);
    setDraftMeta(null);
    setDraftRows([]);
    setProductSearch("");
    setPropagateConfirmOpen(false);
  };

  const buildEntriesPayload = (): { entries: PriceTableEntry[] } | { error: string } => {
    const seenEntries = new Set<string>();
    const entries: PriceTableEntry[] = [];

    // Identidade exibida = nome do produto (normalizado) + unidade + corte. Bloqueia a duplicidade
    // que o usuário vê mesmo com product_id distintos (dois produtos de mesmo nome comercial).
    // Duplicidades já persistidas são toleradas (grandfather) para não travar o save de tabelas
    // legadas; barra-se apenas a INTRODUÇÃO de uma nova ocorrência — espelha a guarda do backend.
    const displayKeyOf = (productName: string, unitId: string, cutTypeId: string) =>
      `${productName.trim().replace(/\s+/g, " ").toUpperCase()}|${unitId}|${cutTypeId || "base"}`;
    const baselineDisplayCounts = new Map<string, number>();
    for (const entry of editorBaseline?.entries ?? []) {
      const baselineProduct = orderedProducts.find((item) => item.id === entry.productId);
      if (!baselineProduct) continue;
      const key = displayKeyOf(baselineProduct.name, entry.unitId, entry.cutTypeId ?? "");
      baselineDisplayCounts.set(key, (baselineDisplayCounts.get(key) ?? 0) + 1);
    }
    const requestedDisplayCounts = new Map<string, number>();

    for (let index = 0; index < draftRows.length; index += 1) {
      const row = draftRows[index];
      const productId = row.productId.trim();
      const unitId = row.unitId.trim();
      const price = Number.parseFloat(row.price.replace(",", "."));

      // Rótulo da linha para as mensagens de erro: nº na ordem exibida + nomes conhecidos.
      const product = orderedProducts.find((item) => item.id === productId);
      const unit = unitById.get(unitId);
      const productLabel = product?.name ?? (productId ? "produto desconhecido" : "produto nao selecionado");
      const unitLabel = unit?.name ?? (unitId ? "unidade desconhecida" : "unidade nao selecionada");
      const where = `Linha ${index + 1} (${productLabel} / ${unitLabel})`;

      if (!productId || !unitId) return { error: `${where}: selecione produto e unidade` };
      if (!Number.isFinite(price) || price <= 0) return { error: `${where}: informe um preço valido` };

      if (!product) return { error: `${where}: produto invalido` };
      if (!unit) return { error: `${where}: unidade invalida` };

      const allowedUnits = getUnitOptionsForProduct(product);
      if (!allowedUnits.some((allowed) => allowed.id === unitId)) {
        const allowedNames = allowedUnits.map((u) => u.name).join(", ") || "nenhuma configurada";
        return {
          error: `${where}: a unidade "${unit.name}" nao pertence ao produto "${product.name}". Unidades permitidas: ${allowedNames}. Ajuste as unidades disponiveis do produto ou troque a unidade desta linha.`,
        };
      }

      const cutTypeId = row.cutTypeId.trim();
      if (cutTypeId && !getCutOptionsForProduct(product).some((cut) => cut.id === cutTypeId)) {
        const cutName = cutTypeById.get(cutTypeId)?.name ?? "selecionado";
        return { error: `${where}: o corte "${cutName}" nao pertence ao produto "${product.name}"` };
      }

      const entryKey = `${productId}:${unitId}:${cutTypeId || "base"}`;
      if (seenEntries.has(entryKey))
        return { error: `${where}: produto repetido com a mesma unidade e corte em outra linha` };

      const displayKey = displayKeyOf(product.name, unitId, cutTypeId);
      const requestedCount = (requestedDisplayCounts.get(displayKey) ?? 0) + 1;
      requestedDisplayCounts.set(displayKey, requestedCount);
      if (requestedCount > 1 && requestedCount > (baselineDisplayCounts.get(displayKey) ?? 0)) {
        return {
          error: `${where}: já existe "${product.name}" com esta unidade e corte nesta tabela. Não é possível cadastrar a mesma combinação (produto + unidade + corte) duas vezes.`,
        };
      }

      seenEntries.add(entryKey);
      entries.push({ productId, unitId, cutTypeId: cutTypeId || null, price: +price.toFixed(2), isOverride: row.isOverride });
    }

    return { entries };
  };

  const hasChanges = useMemo(() => {
    if (!draftMeta || !editorBaseline) return false;
    const metaChanged =
      draftMeta.name.trim() !== editorBaseline.name ||
      draftMeta.scope !== editorBaseline.scope ||
      draftMeta.active !== editorBaseline.active;
    const rowsChanged = JSON.stringify(currentRows) !== JSON.stringify(baselineRows);
    return metaChanged || rowsChanged;
  }, [baselineRows, currentRows, draftMeta, editorBaseline]);

  const performSave = async (propagate: boolean) => {
    if (!editorTable || !draftMeta) return;
    const payload = buildEntriesPayload();
    if ("error" in payload) { toast.error(payload.error); return; }

    setEditorSaving(true);
    try {
      let updated = await updatePriceTable(editorTable.id, {
        name: draftMeta.name.trim(),
        scope: draftMeta.scope,
        active: draftMeta.active,
        entries: payload.entries,
      });

      if (propagate && (updated.children?.length ?? 0) > 0) {
        updated = await propagateToChildren(editorTable.id);
        toast.success("Tabela salva e preços propagados para as derivadas");
      } else {
        toast.success("Tabela atualizada");
      }

      setEditorTable(updated);
      setEditorBaseline(updated);
      setDraftMeta({ name: updated.name, scope: updated.scope, active: updated.active });
      setDraftRows(mapDetailToRows(updated));
      await loadPriceTables();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel salvar a tabela");
    } finally {
      setEditorSaving(false);
    }
  };

  const saveEditor = async () => {
    if (!editorTable || !draftMeta) return;

    if (hasChildren) {
      setPropagateConfirmOpen(true);
      return;
    }

    await performSave(false);
  };

  const removeTable = async () => {
    if (!editorTable) return;
    if (!window.confirm(`Excluir a tabela "${editorTable.name}"?`)) return;

    setEditorSaving(true);
    try {
      await deletePriceTable(editorTable.id);
      await loadPriceTables();
      toast.success("Tabela excluida");
      closeEditor();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel excluir a tabela");
    } finally {
      setEditorSaving(false);
    }
  };

  const addProductRow = () => {
    setDraftRows((rows) => [createNewDraftRow(), ...rows]);
  };

  const updateRow = (key: string, patch: Partial<DraftRow>) => {
    setDraftRows((rows) => rows.map((row) => (row.key !== key ? row : { ...row, ...patch })));
  };

  const updateRowProduct = (key: string, productId: string) => {
    const currentRow = draftRows.find((row) => row.key === key);
    if (!currentRow?.isNew) return;
    const product = orderedProducts.find((item) => item.id === productId);
    if (!product) return;
    const allowedUnits = getUnitOptionsForProduct(product);
    const nextUnitId = allowedUnits.some((unit) => unit.id === currentRow?.unitId)
      ? currentRow?.unitId ?? allowedUnits[0]?.id ?? ""
      : allowedUnits[0]?.id ?? "";
    // Troca de produto invalida o corte (cortes são por produto) — volta para base.
    updateRow(key, { productId, unitId: nextUnitId, cutTypeId: "" });
  };

  const toggleOverride = (key: string) => {
    setDraftRows((rows) =>
      rows.map((row) => (row.key !== key ? row : { ...row, isOverride: !row.isOverride })),
    );
  };

  const removeRow = (key: string) => {
    setDraftRows((rows) => rows.filter((row) => row.key !== key));
  };

  const renderTableRow = (table: PriceTable, highlight = false) => (
    <TableRow
      key={table.id}
      className={cn("cursor-pointer", highlight && "bg-primary/[0.04] hover:bg-primary/10")}
      onClick={() => void openEditor(table.id)}
    >
      <TableCell>
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2 font-display text-sm font-semibold">
            {highlight ? (
              <Star className="h-4 w-4 shrink-0 fill-primary/20 text-primary" />
            ) : (
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            {table.name}
            {highlight && (
              <span className="inline-flex items-center rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary border border-primary/20">
                Padrão
              </span>
            )}
            {table.parentId ? (
              <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 border border-blue-200">
                <GitBranch className="h-2.5 w-2.5" />
                Derivada de {table.parentName} (+{table.markupPercent?.toFixed(2)}%)
              </span>
            ) : (table.childCount ?? 0) > 0 ? (
              <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 border border-violet-200">
                <GitBranch className="h-2.5 w-2.5" />
                Tabela pai · {table.childCount} derivada{table.childCount === 1 ? "" : "s"}
              </span>
            ) : null}
            {/* Stale badge */}
            {table.stale && (
              <span className="inline-flex items-center gap-1 rounded bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium text-warning border border-warning/20">
                <AlertTriangle className="h-2.5 w-2.5" />
                Desatualizada
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">{CLIENT_TYPE_LABELS[table.scope]}</div>
        </div>
      </TableCell>
      <TableCell className="tabular-nums text-sm">{table.entryCount}</TableCell>
      <TableCell>
        {table.linkedCustomerCount === 0 && (table.linkedDeliveryUnitCount ?? 0) === 0 ? (
          <span className="text-xs text-muted-foreground">Nenhum vínculo</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {table.linkedCustomerCount > 0 && (
              <Badge variant="outline" className="text-xs">
                <Building2 className="mr-1 h-3.5 w-3.5" />
                {table.linkedCustomerCount} cliente{table.linkedCustomerCount === 1 ? "" : "s"}
              </Badge>
            )}
            {(table.linkedDeliveryUnitCount ?? 0) > 0 && (
              <Badge variant="outline" className="text-xs">
                <Building2 className="mr-1 h-3.5 w-3.5" />
                {table.linkedDeliveryUnitCount} unidade{table.linkedDeliveryUnitCount === 1 ? "" : "s"}
              </Badge>
            )}
          </div>
        )}
      </TableCell>
      <TableCell>
        <Badge variant={table.active ? "default" : "secondary"}>
          {table.active ? "Ativa" : "Inativa"}
        </Badge>
      </TableCell>
      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          <ExportMenu tableId={table.id} stopPropagation />
          <Button variant="ghost" size="sm" onClick={() => void openEditor(table.id)}>
            <Pencil className="h-4 w-4" />
            Editar
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  const listHeader = (
    <TableHeader className="bg-secondary/40">
      <TableRow>
        <TableHead className="w-[36%]">Nome</TableHead>
        <TableHead className="w-[12%]">Itens</TableHead>
        <TableHead className="w-[22%]">Vínculos</TableHead>
        <TableHead className="w-[12%]">Status</TableHead>
        <TableHead className="w-[18%] text-right">Ações</TableHead>
      </TableRow>
    </TableHeader>
  );

  return (
    <>
      <section className="bg-gradient-hero text-primary-foreground">
        <div className="container py-8 flex flex-col gap-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="font-display text-3xl font-bold tracking-tight">Tabelas de preço</h1>
              <p className="text-primary-foreground/80 text-sm mt-1 max-w-2xl">
                Visualize os clientes vinculados e edite quando necessário.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <SegmentComparisonMenu />
              <CreateTableDialog
                open={listCreateOpen}
                onOpenChange={setListCreateOpen}
                onCreated={(id) => { void loadPriceTables(); void openEditor(id); }}
                priceTables={priceTables}
              />
            </div>
          </div>

          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary-foreground/70" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar por nome ou segmento"
              className="h-11 pl-9 bg-primary-foreground text-foreground placeholder:text-muted-foreground"
            />
          </div>
        </div>
      </section>

      <div className="container py-6">
        {priceTablesStatus === "loading" && priceTables.length === 0 && (
          <div className="surface-card p-8 text-center text-sm text-muted-foreground">
            Carregando tabelas de preço...
          </div>
        )}

        {priceTablesStatus === "error" && priceTables.length === 0 && (
          <div className="surface-card p-8 text-center text-sm text-destructive">
            {priceTablesError ?? "Nao foi possivel carregar as tabelas de preço"}
          </div>
        )}

        {(priceTables.length > 0 || priceTablesStatus === "ready") && (
          <>
          <div className="mb-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSegmentFilter("ALL")}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                segmentFilter === "ALL" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-surface hover:border-primary/40"
              }`}
            >
              Todos os segmentos <span className="opacity-70">({priceTables.length})</span>
            </button>
            {(Object.keys(CLIENT_TYPE_LABELS) as ClientType[]).map((scope) => (
              <button
                key={scope}
                type="button"
                onClick={() => setSegmentFilter(scope)}
                className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                  segmentFilter === scope ? "border-primary bg-primary text-primary-foreground" : "border-border bg-surface hover:border-primary/40"
                }`}
              >
                Linha {CLIENT_TYPE_LABELS[scope]} <span className="opacity-70">({tableCountByScope.get(scope) ?? 0})</span>
              </button>
            ))}
          </div>
          {filteredTables.length === 0 ? (
            <div className="surface-card p-8 text-center text-sm text-muted-foreground">
              Nenhuma tabela encontrada para esse filtro.
            </div>
          ) : (
            <div className="space-y-4">
              {defaultTables.length > 0 && (
                <div className="surface-card overflow-hidden border border-primary/30 ring-1 ring-primary/20">
                  <div className="flex items-center gap-2 border-b border-primary/20 bg-primary/5 px-4 py-2.5">
                    <Star className="h-4 w-4 shrink-0 fill-primary/20 text-primary" />
                    <h2 className="font-display text-sm font-semibold text-primary">Tabelas Padrão</h2>
                    <span className="text-xs text-muted-foreground">
                      base de cada segmento — fixadas no topo
                    </span>
                  </div>
                  <Table>
                    {listHeader}
                    <TableBody>
                      {defaultTables.map((table) => renderTableRow(table, true))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {otherTables.length > 0 && (
                <div className="surface-card overflow-hidden">
                  <Table>
                    {listHeader}
                    <TableBody>
                      {otherTables.map((table) => renderTableRow(table))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
          </>
        )}
      </div>

      <Dialog open={editorOpen} onOpenChange={(open) => { if (!open) closeEditor(); }}>
        <DialogContent className="grid h-[100vh] w-[100vw] max-w-none grid-rows-[auto,1fr,auto] gap-0 overflow-hidden rounded-none p-0 sm:h-[92vh] sm:w-[96vw] sm:rounded-2xl">
          <div className="border-b border-border px-6 py-5">
            <DialogHeader className="text-left">
              <DialogTitle className="font-display text-2xl flex flex-wrap items-center gap-3">
                Editar tabela de preço
                {editorTable && (
                  <>
                    {isChildTable && (
                      <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-sm font-medium text-blue-700 border border-blue-200">
                        <GitBranch className="h-3.5 w-3.5" />
                        Derivada de {editorTable.parentName} (+{editorTable.markupPercent?.toFixed(2)}%)
                      </span>
                    )}
                    {!isChildTable && hasChildren && (
                      <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-2 py-0.5 text-sm font-medium text-violet-700 border border-violet-200">
                        <GitBranch className="h-3.5 w-3.5" />
                        Tabela pai · {editorTable.children?.length} derivada{editorTable.children?.length === 1 ? "" : "s"}
                      </span>
                    )}
                    {editorTable.stale && (
                      <span className="inline-flex items-center gap-1 rounded bg-warning/10 px-2 py-0.5 text-sm font-medium text-warning border border-warning/20">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Desatualizada
                      </span>
                    )}
                  </>
                )}
              </DialogTitle>
              <DialogDescription className="sr-only">Editar tabela de preço</DialogDescription>
            </DialogHeader>
          </div>

          <div className="min-h-0 overflow-y-auto px-6 py-5">
            <div className="space-y-6">
              {editorLoading && (
                <div className="surface-card p-8 text-center text-sm text-muted-foreground">
                  <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
                  Carregando tabela...
                </div>
              )}
              {editorError && !editorLoading && (
                <div className="surface-card p-5 text-sm text-destructive">{editorError}</div>
              )}
              {metadataError && !editorLoading && (
                <div className="surface-card p-5 text-sm text-destructive">{metadataError}</div>
              )}

              {editorTable && draftMeta && !editorLoading && !editorError && (
                <>
                  {editorTable.stale && (
                    <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/5 p-4 text-sm">
                      <AlertTriangle className="h-4 w-4 shrink-0 text-warning mt-0.5" />
                      <div>
                        <p className="font-medium text-warning">Tabela desatualizada</p>
                        <p className="text-muted-foreground mt-0.5">
                          A tabela pai foi alterada. Os preços desta tabela podem estar desatualizados.
                          O administrador pode recalcular propagando novamente a partir da tabela pai.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="surface-card p-4 grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="table-name">Nome</Label>
                      <Input
                        id="table-name"
                        value={draftMeta.name}
                        onChange={(event) =>
                          setDraftMeta((current) => (current ? { ...current, name: event.target.value } : current))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="table-scope">Segmento</Label>
                      <Select
                        value={draftMeta.scope}
                        disabled={isChildTable}
                        onValueChange={(value) =>
                          setDraftMeta((current) => (current ? { ...current, scope: value as ClientType } : current))
                        }
                      >
                        <SelectTrigger id="table-scope">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(CLIENT_TYPE_LABELS) as ClientType[]).map((scope) => (
                            <SelectItem key={scope} value={scope}>
                              {CLIENT_TYPE_LABELS[scope]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="chip"
                          data-active={draftMeta.active}
                          onClick={() => setDraftMeta((current) => (current ? { ...current, active: true } : current))}
                        >
                          Ativa
                        </Button>
                        <Button
                          type="button"
                          variant="chip"
                          data-active={!draftMeta.active}
                          onClick={() => setDraftMeta((current) => (current ? { ...current, active: false } : current))}
                        >
                          Inativa
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Vínculos</Label>
                      <div className="text-sm text-muted-foreground">
                        {linkedClients.length} cliente{linkedClients.length === 1 ? "" : "s"} vinculado
                        {linkedClients.length === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>

                  {!isChildTable && hasChildren && (
                    <div className="surface-card p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Tabelas derivadas ({editorTable.children?.length})
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {editorTable.children?.map((child) => (
                          <div
                            key={child.id}
                            className="flex items-center gap-2 rounded-md border border-border bg-secondary/30 px-3 py-1.5 text-sm cursor-pointer hover:bg-secondary/60 transition-colors"
                            onClick={(e) => { e.stopPropagation(); void openEditor(child.id); }}
                          >
                            <GitBranch className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-medium">{child.name}</span>
                            <span className="text-muted-foreground text-xs">+{child.markupPercent?.toFixed(2)}%</span>
                            {child.stale && (
                              <span className="inline-flex items-center gap-0.5 rounded bg-warning/10 px-1 py-0.5 text-[10px] text-warning">
                                <AlertTriangle className="h-2.5 w-2.5" />
                                Desatualizada
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {linkedClients.length > 0 && (
                    <div className="surface-card p-4">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Clientes vinculados
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {linkedClients.map((client) => (
                          <Badge key={client.id} variant="outline" className="max-w-full">
                            <Users className="mr-1 h-3.5 w-3.5" />
                            {client.companyName ?? client.email}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {linkedDeliveryUnits.length > 0 && (
                    <div className="surface-card p-4">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Unidades de entrega vinculadas
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {linkedDeliveryUnits.map((unit) => (
                          <Badge
                            key={unit.id}
                            variant="outline"
                            className={`max-w-full ${unit.active ? "" : "opacity-60"}`}
                            title={`${unit.customerName} · ${unit.name}${unit.active ? "" : " (inativa)"}`}
                          >
                            <Building2 className="mr-1 h-3.5 w-3.5" />
                            {unit.customerName} · {unit.name}
                            {!unit.active && " (inativa)"}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="surface-card p-4 space-y-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex flex-col gap-3 sm:flex-1 sm:flex-row sm:items-center sm:gap-4">
                        <div className="shrink-0">
                          <h3 className="font-display text-lg font-semibold">Produtos da tabela</h3>
                          {isChildTable && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Entradas com <span className="font-semibold text-amber-600">Personalizado</span> ignoram a propagação do pai.
                            </p>
                          )}
                        </div>
                        <div className="relative w-full sm:max-w-xl sm:flex-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                          <Input
                            value={productSearch}
                            onChange={(event) => setProductSearch(event.target.value)}
                            placeholder="Buscar produto na tabela..."
                            className="h-12 pl-10 pr-10 text-base"
                          />
                          {productSearch && (
                            <button
                              type="button"
                              onClick={() => setProductSearch("")}
                              aria-label="Limpar busca"
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <Button type="button" variant="outline" className="shrink-0" onClick={addProductRow}>
                        <Plus className="h-4 w-4" />
                        Adicionar produto
                      </Button>
                    </div>

                    <div className="overflow-hidden rounded-lg border border-border">
                      <Table>
                        <TableHeader className="bg-secondary/40">
                          <TableRow>
                            <TableHead className="w-[110px]">Código</TableHead>
                            <TableHead>Produto</TableHead>
                            <TableHead className="w-[150px]">Unidade</TableHead>
                            <TableHead className="w-[170px]">Corte</TableHead>
                            <TableHead className="w-[160px]">Preço</TableHead>
                            {isChildTable && <TableHead className="w-[110px]">Override</TableHead>}
                            <TableHead className="w-[92px] text-center">Histórico</TableHead>
                            <TableHead className="w-[72px] text-right">Remover</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {visibleRows.map((row) => {
                            const product = orderedProducts.find((item) => item.id === row.productId) ?? null;
                            const unitOptions = product ? getUnitOptionsForProduct(product) : [];
                            const cutOptions = product ? getCutOptionsForProduct(product) : [];

                            return (
                              <TableRow key={row.key} className={row.isNew ? "bg-primary/5" : undefined}>
                                <TableCell className="tabular-nums text-sm text-muted-foreground">
                                  {product?.code ?? "—"}
                                </TableCell>
                                <TableCell>
                                  {row.isNew ? (
                                    <ProductCombobox
                                      value={row.productId}
                                      options={selectableProducts}
                                      onSelect={(value) => updateRowProduct(row.key, value)}
                                    />
                                  ) : (
                                    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                                      <span className="text-sm font-medium">
                                        {product?.name ?? "Produto indisponivel"}
                                      </span>
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Select
                                    value={row.unitId}
                                    onValueChange={(value) => updateRow(row.key, { unitId: value })}
                                    disabled={!product}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Unidade" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {unitOptions.map((unit) => (
                                        <SelectItem key={unit.id} value={unit.id}>
                                          {unit.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                                <TableCell>
                                  <Select
                                    value={row.cutTypeId || BASE_CUT_VALUE}
                                    onValueChange={(value) =>
                                      updateRow(row.key, { cutTypeId: value === BASE_CUT_VALUE ? "" : value })
                                    }
                                    disabled={!product || cutOptions.length === 0}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Sem corte (base)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={BASE_CUT_VALUE}>Sem corte (base)</SelectItem>
                                      {cutOptions.map((cut) => (
                                        <SelectItem key={cut.id} value={cut.id}>
                                          {cut.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={row.price}
                                    onChange={(event) => updateRow(row.key, { price: event.target.value })}
                                    placeholder="0,00"
                                    className="tabular-nums"
                                  />
                                </TableCell>
                                {isChildTable && (
                                  <TableCell>
                                    <button
                                      type="button"
                                      onClick={() => toggleOverride(row.key)}
                                      title={row.isOverride ? "Remover override — volta ao markup do pai" : "Definir como preço personalizado (ignora markup)"}
                                      className={cn(
                                        "inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors",
                                        row.isOverride
                                          ? "bg-amber-50 text-amber-700 border border-amber-300 hover:bg-amber-100"
                                          : "bg-secondary text-muted-foreground border border-border hover:bg-secondary/80"
                                      )}
                                    >
                                      <Zap className="h-3 w-3" />
                                      {row.isOverride ? "Personalizado" : "Padrão"}
                                    </button>
                                  </TableCell>
                                )}
                                <TableCell className="text-center">
                                  {editorTable && row.entryId ? (
                                    <PriceHistoryDialog
                                      tableId={editorTable.id}
                                      entryId={row.entryId}
                                      productName={product?.name ?? "Produto"}
                                    />
                                  ) : (
                                    <span className="text-[11px] text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeRow(row.key)}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {draftRows.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={isChildTable ? 8 : 7} className="py-10 text-center text-sm text-muted-foreground">
                                Adicione um produto para começar a editar a tabela.
                              </TableCell>
                            </TableRow>
                          )}
                          {draftRows.length > 0 && visibleRows.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={isChildTable ? 8 : 7} className="py-10 text-center text-sm text-muted-foreground">
                                Nenhum produto encontrado para "{productSearch.trim()}".
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="border-t border-border px-6 py-4">
            <DialogFooter className="gap-2 sm:justify-between">
              <div className="flex flex-wrap gap-2">
                {editorTable && <ExportMenu tableId={editorTable.id} variant="outline" size="default" />}
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => void removeTable()}
                  disabled={!editorTable || editorSaving || linkedClients.length > 0 || hasChildren}
                  title={hasChildren ? "Remova as tabelas derivadas primeiro" : undefined}
                >
                  <Trash2 className="h-4 w-4" />
                  Excluir
                </Button>
                {editorTable && !isChildTable && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setChildCreateOpen(true)}
                    disabled={editorSaving}
                  >
                    <GitBranch className="h-4 w-4" />
                    Criar derivada
                  </Button>
                )}
                {editorTable && linkedClients.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setMigrateOpen(true)}
                    disabled={editorSaving}
                    title="Mover clientes vinculados para outra tabela"
                  >
                    <ArrowRightLeft className="h-4 w-4" />
                    Migrar clientes
                  </Button>
                )}
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-2">
                <Button variant="outline" onClick={closeEditor} disabled={editorSaving}>
                  Cancelar
                </Button>
                <Button
                  variant="hero"
                  onClick={() => void saveEditor()}
                  disabled={!editorTable || editorLoading || editorSaving || !hasChanges}
                >
                  {editorSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  Salvar alterações
                </Button>
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={propagateConfirmOpen} onOpenChange={setPropagateConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Propagar preços para tabelas derivadas?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta tabela tem{" "}
              <strong>{editorTable?.children?.length} tabela{editorTable?.children?.length === 1 ? "" : "s"} derivada{editorTable?.children?.length === 1 ? "" : "s"}</strong>:{" "}
              <strong>{editorTable?.children?.map((c) => c.name).join(", ")}</strong>.
              <br /><br />
              Deseja recalcular os preços das derivadas agora (exceto itens com preço personalizado)?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={async () => {
                setPropagateConfirmOpen(false);
                await performSave(false);
              }}
            >
              Salvar só o pai
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={async () => {
                setPropagateConfirmOpen(false);
                await performSave(true);
              }}
            >
              <RefreshCw className="h-4 w-4" />
              Salvar e propagar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editorTable && (
        <CreateChildTableDialog
          open={childCreateOpen}
          onOpenChange={setChildCreateOpen}
          parentId={editorTable.id}
          parentName={editorTable.name}
          onCreated={async () => {
            await loadPriceTables();
            const updated = await fetchPriceTable(editorTable.id);
            setEditorTable(updated);
            setEditorBaseline(updated);
            setDraftMeta({ name: updated.name, scope: updated.scope, active: updated.active });
            setDraftRows(mapDetailToRows(updated));
          }}
        />
      )}

      {editorTable && (
        <MigrateCustomersDialog
          open={migrateOpen}
          onOpenChange={setMigrateOpen}
          sourceTable={editorTable}
          priceTables={priceTables}
          onMigrated={async () => {
            await loadPriceTables();
            const updated = await fetchPriceTable(editorTable.id);
            setEditorTable(updated);
            setEditorBaseline(updated);
            setDraftMeta({ name: updated.name, scope: updated.scope, active: updated.active });
            setDraftRows(mapDetailToRows(updated));
          }}
        />
      )}
    </>
  );
}

function ExportMenu({
  tableId,
  variant = "ghost",
  size = "sm",
  label = "Exportar",
  stopPropagation = false,
}: {
  tableId: string;
  variant?: "ghost" | "outline";
  size?: "sm" | "default";
  label?: string;
  stopPropagation?: boolean;
}) {
  const [exporting, setExporting] = useState<null | "pdf" | "xlsx">(null);

  const run = async (kind: "pdf" | "xlsx") => {
    setExporting(kind);
    try {
      if (kind === "pdf") await exportPriceTablePdf(tableId);
      else await exportPriceTableXlsx(tableId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel exportar a tabela");
    } finally {
      setExporting(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant={variant}
          size={size}
          disabled={exporting !== null}
          onClick={(e) => { if (stopPropagation) e.stopPropagation(); }}
        >
          {exporting !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => { if (stopPropagation) e.stopPropagation(); }}>
        <DropdownMenuItem onSelect={() => void run("pdf")}>
          <FileText className="mr-2 h-4 w-4" />
          Exportar PDF
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void run("xlsx")}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Exportar Excel (.xlsx)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Baixa o comparativo de preços consolidado de um segmento (Tabela Padrão x personalizadas ativas).
 * Somente leitura — apenas dispara o download do XLSX gerado pelo backend.
 */
function SegmentComparisonMenu() {
  const [exporting, setExporting] = useState<ClientType | null>(null);

  const run = async (scope: ClientType) => {
    setExporting(scope);
    try {
      await exportSegmentComparisonXlsx(scope);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel gerar o comparativo");
    } finally {
      setExporting(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="secondary" disabled={exporting !== null}>
          {exporting !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
          Comparativo por segmento
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {(Object.keys(CLIENT_TYPE_LABELS) as ClientType[]).map((scope) => (
          <DropdownMenuItem key={scope} onSelect={() => void run(scope)}>
            <Download className="mr-2 h-4 w-4" />
            {CLIENT_TYPE_LABELS[scope]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MigrateCustomersDialog({
  open,
  onOpenChange,
  sourceTable,
  priceTables,
  onMigrated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceTable: PriceTableDetail;
  priceTables: PriceTable[];
  onMigrated: () => Promise<void>;
}) {
  const clients = sourceTable.linkedCustomers;
  const [targetTableId, setTargetTableId] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Sempre que o diálogo abre, começa com todos os clientes selecionados e destino limpo.
  useEffect(() => {
    if (open) {
      setSelectedIds(clients.map((client) => client.id));
      setTargetTableId("");
    }
  }, [open, clients]);

  const targetOptions = useMemo(
    () => priceTables.filter((table) => table.id !== sourceTable.id),
    [priceTables, sourceTable.id],
  );

  const allSelected = selectedIds.length === clients.length && clients.length > 0;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : clients.map((client) => client.id));
  };

  const toggleOne = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );
  };

  const submit = async () => {
    if (!targetTableId) { toast.error("Selecione a tabela de destino"); return; }
    if (selectedIds.length === 0) { toast.error("Selecione pelo menos um cliente"); return; }

    setSaving(true);
    try {
      await migrateCustomers(sourceTable.id, { targetTableId, customerIds: selectedIds });
      const targetName = targetOptions.find((table) => table.id === targetTableId)?.name ?? "destino";
      toast.success(
        `${selectedIds.length} cliente${selectedIds.length === 1 ? "" : "s"} migrado${selectedIds.length === 1 ? "" : "s"} para "${targetName}"`,
      );
      await onMigrated();
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel migrar os clientes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Migrar clientes
          </DialogTitle>
          <DialogDescription>
            Mova os clientes de <strong>{sourceTable.name}</strong> para outra tabela de preço.
            Escolha o destino e selecione quais clientes migrar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Tabela de destino *</Label>
            <Select value={targetTableId} onValueChange={setTargetTableId}>
              <SelectTrigger><SelectValue placeholder="Selecione a tabela de destino" /></SelectTrigger>
              <SelectContent>
                {targetOptions.map((table) => (
                  <SelectItem key={table.id} value={table.id}>
                    {table.name} · {CLIENT_TYPE_LABELS[table.scope]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Clientes ({selectedIds.length}/{clients.length})</Label>
              <Button type="button" variant="ghost" size="sm" onClick={toggleAll}>
                {allSelected ? "Limpar seleção" : "Selecionar todos"}
              </Button>
            </div>
            <div className="rounded-lg border border-border">
              <ScrollArea className="max-h-64">
                <div className="divide-y divide-border">
                  {clients.map((client) => (
                    <label
                      key={client.id}
                      className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm hover:bg-secondary/40"
                    >
                      <Checkbox
                        checked={selectedIds.includes(client.id)}
                        onCheckedChange={() => toggleOne(client.id)}
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {client.companyName ?? client.email}
                      </span>
                    </label>
                  ))}
                </div>
              </ScrollArea>
            </div>
            {someSelected && (
              <p className="text-xs text-muted-foreground">
                {selectedIds.length === 1
                  ? `Apenas o cliente marcado será movido. Os demais permanecem em ${sourceTable.name}.`
                  : `Apenas os ${selectedIds.length} clientes marcados serão movidos. Os demais permanecem em ${sourceTable.name}.`}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button variant="hero" onClick={() => void submit()} disabled={saving || !targetTableId || selectedIds.length === 0}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4" />}
            Migrar {selectedIds.length > 0 ? `${selectedIds.length} ` : ""}cliente{selectedIds.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductCombobox({
  value,
  options,
  onSelect,
}: {
  value: string;
  options: AdminProductSummary[];
  onSelect: (productId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedProduct = options.find((item) => item.id === value) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between gap-2 overflow-hidden"
        >
          <span className="flex min-w-0 items-center gap-2 truncate">
            {selectedProduct ? (
              <span>{selectedProduct.name}</span>
            ) : (
              <span className="text-muted-foreground">Selecione o produto</span>
            )}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar produto..." />
          <CommandList>
            <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
            <CommandGroup>
              {options.map((item) => (
                <CommandItem
                  key={item.id}
                  value={`${item.name} ${item.category} ${item.code ?? ""}`}
                  onSelect={() => { onSelect(item.id); setOpen(false); }}
                >
                  <Check className={cn("mr-2 h-4 w-4", item.id === value ? "opacity-100" : "opacity-0")} />
                  {item.code != null && (
                    <span className="mr-2 shrink-0 tabular-nums text-xs text-muted-foreground">{item.code}</span>
                  )}
                  <span className="truncate">{item.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CreateTableDialog({
  open,
  onOpenChange,
  onCreated,
  priceTables,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
  priceTables: PriceTable[];
}) {
  const [name, setName] = useState("");
  const [scope, setScope] = useState<ClientType>("RESTAURANTE");
  const [active, setActive] = useState(true);
  const [mode, setMode] = useState<"empty" | "duplicate">("empty");
  const [duplicateFromId, setDuplicateFromId] = useState("");
  const [saving, setSaving] = useState(false);

  const compatible = useMemo(
    () => priceTables.filter((table) => table.scope === scope && !table.parentId),
    [priceTables, scope],
  );

  const submit = async () => {
    if (!name.trim()) { toast.error("Informe o nome da tabela"); return; }
    if (mode === "duplicate" && !duplicateFromId) { toast.error("Selecione uma tabela base"); return; }

    setSaving(true);
    try {
      const table = await createPriceTable({
        name: name.trim(),
        scope,
        active,
        duplicateFromId: mode === "duplicate" ? duplicateFromId : undefined,
      });
      toast.success("Tabela criada");
      onCreated(table.id);
      setName(""); setScope("RESTAURANTE"); setActive(true); setMode("empty"); setDuplicateFromId("");
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel criar a tabela");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="hero">
          <Plus className="h-4 w-4" />
          Nova tabela
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Nova tabela de preço</DialogTitle>
          <DialogDescription className="sr-only">Criar nova tabela de preço</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Nome *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Tabela premium" />
          </div>
          <div className="space-y-2">
            <Label>Segmento *</Label>
            <Select value={scope} onValueChange={(v) => { setScope(v as ClientType); setDuplicateFromId(""); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(CLIENT_TYPE_LABELS) as ClientType[]).map((item) => (
                  <SelectItem key={item} value={item}>{CLIENT_TYPE_LABELS[item]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <div className="flex gap-2">
              <Button type="button" variant="chip" data-active={active} onClick={() => setActive(true)}>Ativa</Button>
              <Button type="button" variant="chip" data-active={!active} onClick={() => setActive(false)}>Inativa</Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Preenchimento inicial</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="chip" data-active={mode === "empty"} onClick={() => setMode("empty")}>Vazia</Button>
              <Button type="button" variant="chip" data-active={mode === "duplicate"} onClick={() => setMode("duplicate")} disabled={compatible.length === 0}>Duplicar</Button>
            </div>
          </div>
          {mode === "duplicate" && (
            <div className="space-y-2">
              <Label>Tabela base *</Label>
              <Select value={duplicateFromId} onValueChange={setDuplicateFromId}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {compatible.map((table) => <SelectItem key={table.id} value={table.id}>{table.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button variant="hero" onClick={() => void submit()} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Criar tabela
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateChildTableDialog({
  open,
  onOpenChange,
  parentId,
  parentName,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentId: string;
  parentName: string;
  onCreated: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [markupPercent, setMarkupPercent] = useState("10");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error("Informe o nome da tabela derivada"); return; }
    const pct = Number.parseFloat(markupPercent.replace(",", "."));
    if (!Number.isFinite(pct)) { toast.error("Informe um markup válido"); return; }

    setSaving(true);
    try {
      const child = await createChildTable(parentId, { name: name.trim(), markupPercent: pct });
      toast.success(`Tabela derivada "${child.name}" criada com +${pct.toFixed(2)}%`);
      await onCreated();
      setName(""); setMarkupPercent("10");
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel criar a tabela derivada");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Criar tabela derivada
          </DialogTitle>
          <DialogDescription>
            Derivada de <strong>{parentName}</strong>. Os preços serão calculados aplicando o markup sobre os preços do pai.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Nome *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Tabela premium +10%" autoFocus />
          </div>
          <div className="space-y-2">
            <Label>Markup sobre o pai *</Label>
            <div className="relative">
              <Input
                type="number"
                step="0.01"
                value={markupPercent}
                onChange={(e) => setMarkupPercent(e.target.value)}
                placeholder="10.00"
                className="pr-8 tabular-nums"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Use valores negativos para descontos (ex.: -5 para 5% abaixo do pai).
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button variant="hero" onClick={() => void submit()} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitBranch className="h-4 w-4" />}
            Criar derivada
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Consulta somente-leitura do histórico de preço de uma entrada: carrega sob demanda (ao abrir) as até
 * 4 últimas alterações reais — de quanto para quanto, quando e por quem. Não edita nem remove nada.
 */
function PriceHistoryDialog({
  tableId,
  entryId,
  productName,
}: {
  tableId: string;
  entryId: string;
  productName: string;
}) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [changes, setChanges] = useState<PriceChange[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setStatus("loading");
    setError(null);
    fetchEntryPriceHistory(tableId, entryId)
      .then((result) => {
        if (!active) return;
        setChanges(result);
        setStatus("ready");
      })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Falha ao carregar o histórico");
        setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [open, tableId, entryId]);

  const formatPrice = (value: number) =>
    value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const formatMoment = (iso: string) =>
    new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="ghost" size="icon" title="Ver histórico de alterações de preço">
          <History className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Histórico de preço
          </DialogTitle>
          <DialogDescription>
            {productName} · todas as alterações (mais recentes primeiro).
          </DialogDescription>
        </DialogHeader>

        {status === "loading" && (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Carregando…
          </div>
        )}
        {status === "error" && (
          <p className="py-6 text-center text-sm text-destructive">{error}</p>
        )}
        {status === "ready" && changes.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Sem alterações registradas.
          </p>
        )}
        {status === "ready" && changes.length > 0 && (
          <ScrollArea className="max-h-[60vh]">
            <ul className="divide-y divide-border pr-3">
              {changes.map((change, index) => (
                <li key={index} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium tabular-nums">
                      <span className="text-muted-foreground line-through">{formatPrice(change.oldPrice)}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{formatPrice(change.newPrice)}</span>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {change.actorEmail} · {change.actorRole}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                    {formatMoment(change.changedAt)}
                  </span>
                </li>
              ))}
            </ul>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
